import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import type { WelfareInitiative } from "../data/welfareInitiatives";
import { getWikimediaSrcSet } from "../utils/wikimediaImage";

interface WelfareInitiativeCardProps {
  initiative: WelfareInitiative;
}

export default function WelfareInitiativeCard({ initiative }: WelfareInitiativeCardProps) {
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5">
      <img
        src={initiative.photoUrl}
        srcSet={getWikimediaSrcSet(initiative.photoUrl)}
        alt={initiative.photoAlt}
        title={initiative.photoCredit}
        loading="lazy"
        className="h-48 w-full object-cover"
      />
      <div className="p-6">
        <span className="text-xs font-semibold uppercase tracking-wide text-teal-600">{initiative.region}</span>
        <h3 className="mt-1 font-display text-lg font-semibold text-ocean-950">{initiative.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-ocean-800/70">{initiative.description}</p>

        {initiative.impactStat && (
          <div className="mt-4 rounded-xl bg-teal-400/10 px-4 py-2 text-center">
            <p className="font-display text-xl font-bold text-teal-700">{initiative.impactStat.value}</p>
            <p className="text-xs text-ocean-800/60">{initiative.impactStat.label}</p>
          </div>
        )}

        {initiative.quote && (
          <blockquote className="mt-4 border-l-2 border-teal-400 pl-3 text-sm italic text-ocean-800/70">
            "{initiative.quote.text}"
            <footer className="mt-1 not-italic text-xs text-ocean-800/50">— {initiative.quote.attribution}</footer>
          </blockquote>
        )}

        {initiative.harbourId && (
          <Link
            to={`/locations?harbour=${initiative.harbourId}#map`}
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700"
          >
            <MapPin className="h-3.5 w-3.5" />
            See this harbour on the map
          </Link>
        )}
      </div>
    </div>
  );
}
